import { useState, useRef } from 'react'
import { X, Upload, Image, DollarSign, Briefcase } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface PortfolioUpdateComposerProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    screenshotUrl?: string
    portfolioValue?: number
    positions?: string
    commentary?: string
  }) => Promise<void>
}

export function PortfolioUpdateComposer({
  open,
  onClose,
  onSubmit,
}: PortfolioUpdateComposerProps) {
  const [portfolioValue, setPortfolioValue] = useState('')
  const [positions, setPositions] = useState('')
  const [commentary, setCommentary] = useState('')
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setScreenshotPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = async () => {
    if (!portfolioValue && !positions && !screenshotPreview) {
      toast.error(
        'Please add at least a portfolio value, positions, or screenshot'
      )
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        screenshotUrl: screenshotPreview || undefined,
        portfolioValue: portfolioValue
          ? parseFloat(portfolioValue.replace(/,/g, ''))
          : undefined,
        positions: positions.trim() || undefined,
        commentary: commentary.trim() || undefined,
      })
      toast.success('Portfolio update posted')
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to create portfolio update:', error)
      toast.error('Failed to post update')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setPortfolioValue('')
    setPositions('')
    setCommentary('')
    setScreenshotPreview(null)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetForm()
          onClose()
        }
      }}
    >
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[98vh] w-full sm:max-w-md p-0">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Briefcase className="w-5 h-5 text-black" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-white">
                Portfolio Update
              </SheetTitle>
              <p className="text-xs text-neutral-400 mt-0.5">
                Share a portfolio snapshot
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              resetForm()
              onClose()
            }}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Screenshot upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">
              Screenshot
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            {screenshotPreview ? (
              <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
                <img
                  src={screenshotPreview}
                  alt="Preview"
                  className="w-full h-auto object-cover"
                />
                <button
                  onClick={() => setScreenshotPreview(null)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-32 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-emerald-500/30 bg-neutral-900/30 hover:bg-emerald-500/5 flex flex-col items-center justify-center gap-2 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center">
                  <Image className="w-5 h-5 text-neutral-400" />
                </div>
                <span className="text-neutral-500 text-sm">
                  Upload screenshot
                </span>
              </button>
            )}
          </div>

          {/* Portfolio value */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">
              Portfolio Value
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                value={portfolioValue}
                onChange={(e) => setPortfolioValue(e.target.value)}
                placeholder="1,769,538.93"
                className="pl-9 bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-600 h-11 font-mono"
              />
            </div>
          </div>

          {/* Positions */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">
              Positions
            </label>
            <textarea
              value={positions}
              onChange={(e) => setPositions(e.target.value)}
              placeholder={
                'USD — $1,618,342.40 (91.46%)\nGEV — 118 shares @ $846.99\nVRT — 191 shares @ $261.64'
              }
              rows={5}
              className="w-full rounded-lg bg-neutral-900/80 border border-white/[0.08] text-white placeholder:text-neutral-600 px-3 py-2.5 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
            />
          </div>

          {/* Commentary */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-300">
              Commentary{' '}
              <span className="text-neutral-600 font-normal">(optional)</span>
            </label>
            <textarea
              value={commentary}
              onChange={(e) => setCommentary(e.target.value)}
              placeholder="Brief market outlook or portfolio notes..."
              rows={3}
              className="w-full rounded-lg bg-neutral-900/80 border border-white/[0.08] text-white placeholder:text-neutral-600 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex-shrink-0 p-5 border-t border-white/[0.06]">
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              (!portfolioValue && !positions && !screenshotPreview)
            }
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25 disabled:opacity-40"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Posting...
              </div>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Post Portfolio Update
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
